import { isValidEmail } from '@lender/utils';
import DOMPurify from 'dompurify';
import { convert } from 'html-to-text';
import _ from 'lodash';

import { ActivityOriginType, ActivityType, EmailStateType } from 'constants/activities';
import { ExternalContactsTitles } from 'constants/contacts';

import { normalizeViewPhoneNumber } from './data';
import { getPersonFullName } from './loanApplication';
import { toDialableNumber } from './phone';

// Keeps each Accounts user search small; the filter itself has no cap.
const USER_SEARCH_FILTER_MAX_VALUES = 100;

const US_DIALABLE_NUMBER_PATTERN = /^\+1\d{10}$/;

// Accounts rejects the whole search with a 400 when any number is not a US one.
const toUsDialableNumber = phoneNumber => {
  const dialableNumber = toDialableNumber(phoneNumber);

  return US_DIALABLE_NUMBER_PATTERN.test(dialableNumber) ? dialableNumber : null;
};

const getActivityEmailAddresses = activities =>
  _.filter(activities, { type: ActivityType.EMAIL }).flatMap(
    ({ from, to = [], cc = [], bcc = [] }) => [from, ...to, ...cc, ...bcc],
  );

const getActivityPhoneNumbers = activities =>
  _.filter(activities, { type: ActivityType.CALL }).flatMap(
    ({ phoneNumberFrom, phoneNumberTo }) => [phoneNumberFrom, phoneNumberTo],
  );

// lookedUpValues: values an earlier search already answered, matched or not — not searched again.
export const getActivityContactLookup = (activities, knownContacts, lookedUpValues = []) => {
  const lookedUp = new Set(lookedUpValues);
  const knownEmails = new Set(knownContacts.map(({ value }) => _.toLower(value)));
  // Exact match, like getFullNameByNumber: a number known only in another format still shows raw.
  const knownPhoneNumbers = new Set(_.map(knownContacts, 'phoneNumber'));

  const emailAddresses = _.uniq(
    getActivityEmailAddresses(activities)
      .map(email => _.toLower(email))
      .filter(email => isValidEmail(email) && !knownEmails.has(email) && !lookedUp.has(email)),
  );

  const phoneNumbers = _.uniq(
    getActivityPhoneNumbers(activities)
      .filter(phoneNumber => !knownPhoneNumbers.has(phoneNumber))
      .map(toUsDialableNumber)
      .filter(phoneNumber => !_.isNull(phoneNumber) && !lookedUp.has(phoneNumber)),
  );

  return { emailAddresses, phoneNumbers };
};

// Accounts ANDs its filters, so emails and phones go in separate searches.
export const getContactUserSearchFilters = ({ emailAddresses, phoneNumbers }) =>
  _.chunk(emailAddresses, USER_SEARCH_FILTER_MAX_VALUES)
    .map(chunk => ({ emailAddresses: chunk }))
    .concat(
      _.chunk(phoneNumbers, USER_SEARCH_FILTER_MAX_VALUES).map(chunk => ({ phoneNumbers: chunk })),
    );

export const getInvalidEmails = emails =>
  emails.filter(email => !isValidEmail(String(email ?? '')));

const htmlConvertingOptions = {
  selectors: [
    { selector: 'img', format: 'skip' },
    { selector: 'a', options: { ignoreHref: true } },
  ],
};

export const emailContentToString = body =>
  convert(DOMPurify.sanitize(body), htmlConvertingOptions);

export const contactsMapper = (contacts, borrowersContacts) => {
  const [primaryBorrower, ...coBorrowers] = borrowersContacts;

  const mappedBorrowers = [
    {
      label: primaryBorrower.fullName
        ? `${primaryBorrower.fullName} <${primaryBorrower.emailAddress}>`
        : `<${primaryBorrower.emailAddress}>`,
      value: primaryBorrower.emailAddress,
      description: 'Borrower',
      phoneNumber: primaryBorrower.phoneNumber,
      fullName: primaryBorrower.fullName,
    },
    ...coBorrowers
      .map(({ fullName, emailAddress, phoneNumber }) => ({
        label: fullName ? `${fullName} <${emailAddress}>` : `<${emailAddress}>`,
        value: emailAddress,
        description: 'Co-Borrower',
        fullName,
        phoneNumber,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const mappedContacts = contacts
    .map(({ emailAddress, firstName, lastName, phoneNumber, externalContactType, roleName }) => {
      const description = externalContactType
        ? ExternalContactsTitles[externalContactType]
        : roleName;
      const fullName = getPersonFullName({ firstName, lastName }) || '';

      return {
        label: fullName ? `${fullName} <${emailAddress}>` : `<${emailAddress}>`,
        value: emailAddress,
        description,
        fullName,
        phoneNumber,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return _.uniqBy(mappedBorrowers.concat(mappedContacts), 'value').filter(
    borrower => !!borrower.value,
  );
};

export const callContactsMapper = (contacts, borrowersContacts) => {
  const [primaryBorrower, ...coBorrowers] = borrowersContacts;

  const mappedBorrowers = [
    {
      description: 'Borrower',
      fullName: primaryBorrower.fullName,
      phoneNumber: primaryBorrower.phoneNumber,
      emailAddress: primaryBorrower.emailAddress,
    },
    ...coBorrowers
      .map(({ fullName, emailAddress, phoneNumber }) => ({
        description: 'Co-Borrower',
        fullName,
        phoneNumber,
        emailAddress,
      }))
      .sort((a, b) => a.fullName.localeCompare(b.fullName)),
  ];
  const mappedContacts = contacts
    .filter(({ phoneNumber }) => !!phoneNumber)
    .map(({ emailAddress, firstName, lastName, roleName, externalContactType, phoneNumber }) => {
      const description = externalContactType
        ? ExternalContactsTitles[externalContactType]
        : roleName;

      return {
        description,
        fullName: getPersonFullName({ firstName, lastName }) || '',
        phoneNumber: normalizeViewPhoneNumber(phoneNumber),
        emailAddress,
      };
    })
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  return _.uniqBy(mappedBorrowers.concat(mappedContacts), 'emailAddress');
};

const getIsNewEmail = (originType, currentUserId) => {
  if (originType.type === ActivityOriginType.IMPORTED) {
    if (originType.state.type === EmailStateType.NONE) {
      return true;
    }

    if (originType.state.type === EmailStateType.READ) {
      return !originType.state.reads.some(({ userId }) => userId === currentUserId);
    }
  }

  return false;
};

const getEmailEvents = (originType, status) => {
  const recipients = originType.type === ActivityOriginType.TRACKED ? originType.recipients : [];
  const recipientsEvents = recipients.flatMap(({ evensList }) => evensList || []);
  const isEmailReceived =
    originType.type === ActivityOriginType.IMPORTED && originType.isSentFromExternalMailbox;
  const firstEvent = {
    type: isEmailReceived ? 'Received' : 'Sent',
    eventDateTime: status.completeDate,
  };

  return recipientsEvents
    .map(({ type, eventTime }) => ({ type, eventDateTime: eventTime }))
    .concat(firstEvent);
};

export const mapEmailActivities = (emails = [], currentUserId) =>
  emails.map(item => ({
    ..._.pick(item, ['id', 'cc', 'bcc', 'from', 'to', 'subject']),
    preview: item.emailBody?.preview,
    type: ActivityType.EMAIL,
    activityDateTime: item.status.completeDate,
    attachmentNames: item.attachments.map(({ name }) => name),
    linkedResourceDescriptions: item.linkedResources.map(({ name, resourceId }) => ({
      fileName: name,
      resourceId,
    })),
    threadId: item.metadata.ThreadId ? item.metadata.ThreadId[0] : null,
    isNewEmail: getIsNewEmail(item.originType, currentUserId),
    events: getEmailEvents(item.originType, item.status),
  }));

export const mapCallActivities = (calls = []) =>
  calls
    .map(item => ({
      ...item,
      type: ActivityType.CALL,
      activityDateTime: item.dateTime,
      phoneNumberTo: item.phoneNumberTo?.trim(),
      phoneNumberFrom: item.phoneNumberFrom?.trim(),
      recordingUrl: item.recordingUrl?.trim(),
    }))
    .filter(item => item.recordingUrl || item.phoneNumberTo || item.phoneNumberFrom);
